import { formatDateTime } from '../../utils/formatDate';
import {
    formatBootTrace,
    readBootTrace,
    readPreviousBootTrace
} from '../../utils/bootTrace';
import {
    formatDatabaseListing,
    formatOpenProfile,
    readDatabaseListing,
    readOpenProfile,
    readPreviousDatabaseListing,
    readPreviousOpenProfile
} from '../../db/openProfile';
import {
    formatOpenSteps,
    readOpenSteps,
    readPreviousOpenSteps
} from '../../db/openSteps';
import { formatMigrationProgress } from '../../db/migrationProgress';
import { RAW_DATABASE_PREFIX } from '../../db/openStall';
import { withTimeout } from '../../utils/withTimeout';
import { nativeShellVersion } from '../../lib/nativeApp';

/**
 * The lines every report about this app starts with.
 *
 * Which version, which device, which address, and how far the last start-up
 * got. None of it is anything the person holding the tablet could be asked to
 * recite, and all of it decides what a fault means: the same message on one
 * version is a known bug and on the next a regression.
 *
 * Deliberately NOT translated: a report is written to be forwarded to whoever
 * fixes the app, so it has to read the same whatever the device is set to.
 */
export function deviceLines(): string[] {
    const shellVersion = nativeShellVersion();
    return [
        `Zeitpunkt: ${formatDateTime(Date.now())}`,
        `Version: ${__APP_VERSION__}`,
        ...(shellVersion ? [`Android-App: ${shellVersion}`] : []),
        `Adresse: ${typeof window === 'undefined' ? '—' : window.location.href}`,
        `Browser: ${typeof navigator === 'undefined' ? '—' : navigator.userAgent}`
    ];
}

/**
 * How the start-up went, this time and last.
 *
 * The previous attempt matters more than it looks: after a hang the device is
 * reloaded, and by then the interesting run is the one before (see
 * `utils/bootTrace`).
 */
export function bootLines(): string[] {
    const previous = formatBootTrace(readPreviousBootTrace());
    const lines = [`Start: ${formatBootTrace(readBootTrace()) || '—'}`];
    if (previous !== '') {
        lines.push(`Vorheriger Start: ${previous}`);
    }
    return lines;
}

/**
 * Where the database open is, collection by collection — this time and the
 * time before (see db/openProfile).
 */
export function databaseOpenLines(): string[] {
    const indent = (line: string) => `  ${line}`;
    const lines = [
        ...formatOpenProfile(readOpenProfile(), performance.now()),
        ...formatOpenSteps(readOpenSteps(), performance.now()),
        formatMigrationProgress(),
        formatDatabaseListing(readDatabaseListing())
    ]
        .filter((line) => line !== null)
        .map(indent);
    const previous = [
        ...formatOpenProfile(readPreviousOpenProfile()),
        ...formatOpenSteps(readPreviousOpenSteps()),
        formatDatabaseListing(readPreviousDatabaseListing())
    ]
        .filter((line) => line !== null)
        .map(indent);
    return [
        ...(lines.length > 0 ? ['Datenbank, dieser Start:', ...lines] : []),
        ...(previous.length > 0
            ? ['Datenbank, vorheriger Start:', ...previous]
            : [])
    ];
}

/**
 * How this document's start went, for anybody who asks — the boot trace and
 * the database open, this time and the time before. Shown in the sync details
 * and part of the report copied from there.
 */
export function startupLines(): string[] {
    return [...bootLines(), ...databaseOpenLines()];
}

/** How long any one of the questions below may take; the report goes anyway. */
const ENVIRONMENT_TIMEOUT_MS = 2000;

/** Ask the browser something that may throw, reject or never answer. */
function ask<T>(question: () => Promise<T> | undefined): Promise<T | null> {
    try {
        const answer = question();
        return answer
            ? withTimeout(
                  answer.catch(() => null),
                  ENVIRONMENT_TIMEOUT_MS,
                  null
              )
            : Promise.resolve(null);
    } catch {
        return Promise.resolve(null);
    }
}

function megabytes(bytes: number | undefined): string {
    return bytes === undefined ? '?' : `${Math.round(bytes / 1024 / 1024)} MB`;
}

/**
 * What the storage around the database looks like: how full it is, whether the
 * browser may evict it, how many raw databases the app has left lying about
 * (old schema versions, parked generations), and whether a new build is being
 * installed at this very moment.
 */
export async function storageLines(): Promise<string[]> {
    // A report about a fault must never raise one: storage disabled leaves
    // `indexedDB` null, and any of these may throw instead of rejecting.
    if (typeof navigator === 'undefined') {
        return [];
    }
    const [estimate, persisted, databases, registration] = await Promise.all([
        ask(() => navigator.storage?.estimate?.()),
        ask(() => navigator.storage?.persisted?.()),
        ask(() =>
            typeof indexedDB !== 'undefined' && indexedDB?.databases
                ? indexedDB.databases()
                : undefined
        ),
        ask(() => navigator.serviceWorker?.getRegistration?.())
    ]);
    const lines: string[] = [];
    if (estimate) {
        lines.push(
            `Speicher: ${megabytes(estimate.usage)} von ${megabytes(estimate.quota)} belegt` +
                (persisted === null
                    ? ''
                    : persisted
                      ? ', dauerhaft'
                      : ', nicht dauerhaft')
        );
    }
    if (databases) {
        const own = databases.filter((database) =>
            RAW_DATABASE_PREFIX.test(database.name ?? '')
        );
        lines.push(
            `IndexedDB-Datenbanken der App: ${own.length} (insgesamt ${databases.length})`
        );
    }
    if (registration) {
        const worker = registration.installing
            ? 'neue Version wird installiert'
            : registration.waiting
              ? 'neue Version wartet'
              : registration.active
                ? 'aktiv'
                : 'keiner';
        lines.push(`Service Worker: ${worker}`);
    }
    return lines;
}
